/**
 * Campaign params carried in from an ad or landing-page link, e.g.
 * `https://heybesafe.com/safe/?src=gdn-families-aug&promo=WELCOME20`.
 *
 * These must be captured at boot, BEFORE AuthGuard's redirect: that redirect
 * navigates to a bare `/auth` and drops the query string entirely, which is why
 * campaign attribution was previously lost on every ad click.
 *
 * sessionStorage, deliberately — the campaign belongs to THIS visit. It
 * survives the redirect, SPA navigation, account creation and a reload, but
 * does not haunt the browser weeks later. It also sidesteps the logout wipe in
 * safe-store's clearAllLocalData(), which only touches localStorage.
 *
 * Uses the same `hbs:promo` key name as sub-app/src/lib/promoOrigin.ts —
 * different origin, so no collision — but deliberately does NOT mirror that
 * module's behaviour of clearing on a param-less arrival. AuthGuard forwards
 * only `src` (not `promo`) when it bounces an unauthenticated visitor to
 * `/auth`, and the app hard-navigates to param-less URLs in normal use
 * (e.g. ForgotPasswordPage's post-recovery `window.location.assign('/safe')`).
 * Clearing here would wipe a just-captured campaign on the very next same-tab
 * navigation. A genuinely new visit gets a new tab, which has empty
 * sessionStorage anyway — so tab scoping already handles the staleness a
 * clear-on-absence rule would otherwise guard against.
 */

export const CAMPAIGN_SRC_KEY = 'hbs:campaignSrc';
export const CAMPAIGN_PROMO_KEY = 'hbs:promo';

/**
 * Same charset the server enforces on a signup source
 * (`server/functions/auth/index.ts`). The single web-side copy, deliberately —
 * it previously existed in three unlinked places, and a client copy drifting
 * MORE permissive than the server's is the dangerous direction: the value would
 * pass here, be sent as `signupSource`, and be dropped server-side, costing the
 * attribution this whole module exists to preserve.
 *
 * The server stays authoritative and keeps its own independent copy — that is
 * correct, not duplication to collapse. A client must never be the only thing
 * standing between user input and storage.
 */
const SRC_RE = /^[a-z0-9_-]{1,64}$/;

/** True when `value` is a campaign source the server will accept. */
// Type predicate, not plain boolean, so a guarded caller narrows away the
// null/undefined without a redundant second check or a cast.
export function isValidCampaignSrc(value: string | null | undefined): value is string {
  return !!value && SRC_RE.test(value);
}

/**
 * Record this arrival's campaign params. Call once at boot. Only writes when
 * the arrival actually carries a param — never clears. See the module doc
 * comment above for why a param-less arrival must NOT wipe a prior capture.
 */
export function captureCampaignParams(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    write(CAMPAIGN_SRC_KEY, params.get('src'));
    write(CAMPAIGN_PROMO_KEY, params.get('promo'));
  } catch {
    /* storage blocked or URL unparseable → no campaign context */
  }
}

function write(key: string, value: string | null): void {
  if (value) sessionStorage.setItem(key, value);
}

/**
 * The campaign source for this visit, or null. Validated against the server's
 * charset here rather than at the call site, so an invalid tag is simply
 * absent instead of being rejected later by the API.
 */
export function getCampaignSrc(): string | null {
  const raw = read(CAMPAIGN_SRC_KEY);
  return isValidCampaignSrc(raw) ? raw : null;
}

/**
 * The campaign promo code for this visit, or null. Opaque — the server
 * validates it against Stripe at checkout, so there is nothing useful to check
 * here beyond presence.
 */
export function getCampaignPromo(): string | null {
  return read(CAMPAIGN_PROMO_KEY);
}

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
